import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Header } from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { VideoCard } from "@/components/VideoCard";
export default function Ayuda() {
  const canonicalUrl = typeof window !== "undefined" ? `${window.location.origin}/ayuda` : "/ayuda";
  const [terms, setTerms] = useState<string>("");
  const [openTerms, setOpenTerms] = useState(false);
  const [privacy, setPrivacy] = useState<string>("");
  const [openPrivacy, setOpenPrivacy] = useState(false);
  useEffect(() => {
    fetch("/terms.md")
      .then((res) => res.text())
      .then(setTerms)
      .catch((err) => console.error("Failed to load terms.md", err));
  }, []);
  useEffect(() => {
    fetch("/privacy.md")
      .then((res) => res.text())
      .then(setPrivacy)
      .catch((err) => console.error("Failed to load privacy.md", err));
  }, []);
  useEffect(() => {
    const handleHashTarget = () => {
      const hash = window.location.hash?.toLowerCase();
      if (!hash) return;
      const termTargets = ["#terminos-condiciones", "#términos-y-condiciones", "#terminos", "#terms"];
      const privacyTargets = ["#privacy", "#privacidad", "#política-de-privacidad", "#politica-de-privacidad"];
      const soporteTargets = ["#soporte", "#support"];
      if (termTargets.includes(hash)) {
        setOpenTerms(true);
        const el = document.getElementById("terminos-condiciones");
        if (el) {
          el.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }
      if (privacyTargets.includes(hash)) {
        setOpenPrivacy(true);
        const el = document.getElementById("privacy");
        if (el) {
          el.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }
      if (soporteTargets.includes(hash)) {
        const el = document.getElementById("soporte");
        if (el) {
          el.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }
    };

    // Run on mount
    handleHashTarget();

    // React to future hash changes
    window.addEventListener("hashchange", handleHashTarget);
    return () => window.removeEventListener("hashchange", handleHashTarget);
  }, []);
  return (
    <>
      <Helmet>
        <title>DoctIA | Ayuda</title>
        <meta
          name="description"
          content="Aprende a instalar el complemento en Word y sigue nuestro tutorial de uso con imágenes, video y pasos claros."
        />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <Header />

      <main className="container max-w-6xl mx-auto px-6 py-10">
        <h1 className="text-3xl font-bold text-foreground mb-6">Ayuda y Guías de Uso</h1>

        {/* Video Guides Section - Always visible by default */}
        <section className="space-y-6 mb-8" aria-labelledby="videos">
          <Card>
            <CardContent className="pt-6">
              <Accordion type="single" collapsible defaultValue="videos">
                <AccordionItem value="videos" className="border-none">
                  <AccordionTrigger className="text-2xl font-bold hover:no-underline">
                    Guías y tutoriales en video
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-6 pt-4">
                      <VideoCard
                        thumbnailSrc="/images/help/tutorial-doctia-thumb.jpg"
                        title="📹 Cómo usar DoctIA"
                        description="Mirá este video para ver DoctIA en acción"
                        instagramUrl="https://www.instagram.com/inteligencialegal.ok/reel/DQClBLgket3"
                        gradientFrom="from-primary/10"
                        gradientTo="to-accent/10"
                      />

                      <VideoCard
                        thumbnailSrc="/images/help/tutorial-word-thumb.jpg"
                        title="📹 Instalación y uso en Word"
                        description="Seguí el paso a paso en video"
                        instagramUrl="https://www.instagram.com/inteligencialegal.ok/reel/DQJ0SOEkU5-"
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </section>

        {/* Tutorial de uso - Now second */}
        <section className="space-y-6" aria-labelledby="tutorial-uso">
          <Card>
            <CardContent className="pt-6">
              <Accordion type="single" collapsible defaultValue="">
                <AccordionItem value="tutorial" className="border-none">
                  <AccordionTrigger className="text-2xl font-bold hover:no-underline">
                    Paso a paso: Cómo usar DoctIA
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-8 pt-4">
                      <p className="text-base text-muted-foreground bg-secondary/50 p-4 rounded-lg border-l-4 border-primary">
                        DoctIA no es un buscador tradicional. Es una herramienta que entiende lo que estás buscando y te
                        trae exactamente lo que necesitás.
                      </p>

                      {/* Búsqueda simple */}
                      <div className="space-y-4 p-6 bg-secondary/30 rounded-xl border border-border">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-lg">
                            1
                          </div>
                          <h3 className="text-xl font-bold text-foreground">Búsqueda simple y directa</h3>
                        </div>
                        <p className="text-base text-foreground/90 leading-relaxed">
                          Empecemos con algo básico. Escribís algo como <strong>"daño moral 1741"</strong> en el
                          buscador. Técnicamente, para este tipo de consultas genéricas no necesitarías inteligencia
                          artificial. Pero el que puede más, puede lo menos. Y DoctIA lo hace perfectamente.
                        </p>
                        <p className="text-base text-foreground/90 leading-relaxed">
                          En segundos tenés resultados, con un{" "}
                          <strong>resumen claro de las cuestiones debatidas</strong> y las doctrinas o sumarios del
                          caso. Fijate esto: cada resultado incluye el{" "}
                          <strong>tribunal, el número de causa y la fecha</strong>, todo listo para citar.
                        </p>
                        <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                          <p className="text-sm font-semibold text-foreground">
                            ✨ Y lo más importante: podés <strong>bajar el fallo con un solo clic</strong>, sin perder
                            tiempo revisando portales o PDFs escondidos.
                          </p>
                        </div>
                      </div>

                      {/* Búsqueda compleja */}
                      <div className="space-y-4 p-6 bg-secondary/30 rounded-xl border border-border">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-lg">
                            2
                          </div>
                          <h3 className="text-xl font-bold text-foreground">Búsqueda en lenguaje natural</h3>
                        </div>
                        <p className="text-base text-foreground/90 leading-relaxed">
                          Ahora probá algo más complejo. Escribís:{" "}
                          <strong>"responsabilidad civil de la clínica por no indicar diagnóstico genético"</strong>.
                        </p>
                        <p className="text-base text-foreground/90 leading-relaxed">
                          De nuevo, en instantes tenés los resultados. Pero acá viene lo importante:{" "}
                          <strong>DoctIA no buscó palabras sueltas. Entendió la idea completa.</strong>
                        </p>
                        <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                          <p className="text-sm text-foreground leading-relaxed">
                            Si escribieras esto con esta naturalidad en un buscador legal tradicional, te aparecería
                            cualquier cosa. Pero acá, los resultados están en contexto.{" "}
                            <strong>DoctIA interpreta lo que estás diciendo, no cada palabra aislada.</strong>
                          </p>
                        </div>
                      </div>

                      {/* Búsqueda por pregunta */}
                      <div className="space-y-4 p-6 bg-secondary/30 rounded-xl border border-border">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-lg">
                            3
                          </div>
                          <h3 className="text-xl font-bold text-foreground">Hacé preguntas directas</h3>
                        </div>
                        <p className="text-base text-foreground/90 leading-relaxed">
                          Y mirá esto también: podés buscar haciendo una pregunta. Por ejemplo:{" "}
                          <strong>"¿corresponde indexar las indemnizaciones por despido?"</strong>
                        </p>
                        <p className="text-base text-foreground/90 leading-relaxed">
                          Fijate cómo los resultados traen fallos donde se discutió justamente esa cuestión. La
                          inconstitucionalidad de la indexación durante la vigencia de la ley de convertibilidad, por
                          ejemplo.
                        </p>
                        <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                          <p className="text-sm font-semibold text-foreground">
                            💡 En vez de navegar entre miles de resultados, DoctIA te da directamente lo relevante.
                            Porque no se trata de cantidad, sino de <strong>precisión</strong>.
                          </p>
                        </div>
                      </div>

                      {/* Resúmenes con IA */}
                      <div className="space-y-4 p-6 bg-secondary/30 rounded-xl border border-border">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-lg">
                            4
                          </div>
                          <h3 className="text-xl font-bold text-foreground">Resumí fallos con IA</h3>
                        </div>
                        <p className="text-base text-foreground/90 leading-relaxed">
                          Desde los resultados de búsqueda, podés usar el botón <strong>"Resumir con IA"</strong> para
                          obtener un análisis claro y estructurado del fallo. La IA extrae los hechos principales, la
                          cuestión jurídica debatida, los argumentos de las partes y la decisión del tribunal.
                        </p>
                        <p className="text-base text-foreground/90 leading-relaxed">
                          Es como tener un pasante que lee el fallo completo y te hace un informe ejecutivo en segundos.
                        </p>
                      </div>

                      {/* Historial */}
                      <div className="space-y-4 p-6 bg-secondary/30 rounded-xl border border-border">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-lg">
                            5
                          </div>
                          <h3 className="text-xl font-bold text-foreground">Consultá tu historial</h3>
                        </div>
                        <p className="text-base text-foreground/90 leading-relaxed">
                          Todas tus búsquedas y resúmenes quedan guardados en tu <strong>Historial</strong>. Podés
                          volver a consultarlos cuando quieras, sin gastar créditos adicionales.
                        </p>
                        <p className="text-base text-foreground/90 leading-relaxed">
                          Es ideal para retomar casos antiguos o compartir resultados con tu equipo.
                        </p>
                      </div>

                      {/* Call to action */}
                      <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 text-center">
                        <h4 className="text-lg font-semibold text-foreground mb-2">¿Listo para probarlo?</h4>
                        <p className="text-base text-muted-foreground mb-4">
                          Explorá con tus propios casos, preguntas o ideas, y fijate cómo responde. Vas a notar la
                          diferencia enseguida.
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Gestioná tu suscripción y créditos desde el menú <strong>"Gestionar Suscripción"</strong>.
                        </p>
                      </div>

                      {/* Still have questions */}
                      <div className="bg-secondary/30 border border-border rounded-lg p-6 text-center">
                        <div className="flex items-center justify-center gap-2 mb-3">
                          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="text-primary-foreground"
                            >
                              <circle cx="12" cy="12" r="10" />
                              <path d="M12 8v4l2 2" />
                            </svg>
                          </div>
                          <h4 className="text-lg font-semibold text-foreground">¿Todavía tenés dudas?</h4>
                        </div>
                        <p className="text-base text-muted-foreground mb-3">
                          ¡Queremos ayudarte a que tengas la mejor experiencia!
                        </p>
                        <a
                          href="#soporte"
                          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:bg-primary-hover transition-colors"
                        >
                          ¡Contactanos!
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M7 17l9.2-9.2M17 17V7H7" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </section>

        {/* Guía de instalación - Now third */}
        <section className="space-y-6 mt-8" aria-labelledby="instalacion-word">
          <Card>
            <CardContent className="pt-6">
              <Accordion type="single" collapsible defaultValue="">
                <AccordionItem value="instalacion" className="border-none">
                  <AccordionTrigger className="text-2xl font-bold hover:no-underline">
                    Paso a paso: Guía de instalación en Word
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-8 pt-4">
                      <p className="text-base text-muted-foreground bg-secondary/50 p-4 rounded-lg border-l-4 border-primary">
                        ¡Instalar DoctIA en tu Word (365/2016+) es súper fácil!
                      </p>

                      <div className="space-y-10">
                        {/* Step 1 */}
                        <div className="flex flex-col lg:flex-row items-center gap-6 p-6 bg-secondary/30 rounded-xl border border-border">
                          <div className="flex-1 space-y-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-lg">
                                1
                              </div>
                              <h3 className="text-xl font-bold text-foreground">Abrí Word</h3>
                            </div>
                            <p className="text-lg text-foreground/90">
                              Abrí cualquier documento de Word o creá uno nuevo.
                            </p>
                          </div>
                          <div className="w-full lg:w-80">
                            <img
                              src="/images/help/instalacion_office1.jpg"
                              alt="Paso 1: Abrir Word"
                              className="w-full h-auto rounded-lg shadow-card border border-border"
                              loading="lazy"
                            />
                          </div>
                        </div>

                        {/* Step 2 */}
                        <div className="flex flex-col lg:flex-row-reverse items-center gap-6 p-6 bg-secondary/30 rounded-xl border border-border">
                          <div className="flex-1 space-y-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-lg">
                                2
                              </div>
                              <h3 className="text-xl font-bold text-foreground">Accedé a Complementos</h3>
                            </div>
                            <p className="text-lg text-foreground/90">
                              A la derecha del menú principal ("Inicio" o "Home") vas a encontrar el ícono
                              "Complementos" (o "Add-ins"). Hacé click ahí y luego en <strong>Más complementos</strong>.
                            </p>
                          </div>
                          <div className="w-full lg:w-80">
                            <img
                              src="/images/help/instalacion_office2.jpg"
                              alt="Paso 2: Acceder a Complementos"
                              className="w-full h-auto rounded-lg shadow-card border border-border"
                              loading="lazy"
                            />
                          </div>
                        </div>

                        {/* Step 3 */}
                        <div className="flex flex-col lg:flex-row items-center gap-6 p-6 bg-secondary/30 rounded-xl border border-border">
                          <div className="flex-1 space-y-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-lg">
                                3
                              </div>
                              <h3 className="text-xl font-bold text-foreground">Buscá DoctIA</h3>
                            </div>
                            <p className="text-lg text-foreground/90">
                              Una vez adentro, simplemente tenés que buscar <strong>DOCTIA</strong> como se ve en la
                              imagen y hacer click en <strong>"Agregar"</strong>.
                            </p>
                          </div>
                          <div className="w-full lg:w-80">
                            <img
                              src="/images/help/instalacion_office3.jpg"
                              alt="Paso 3: Buscar DoctIA"
                              className="w-full h-auto rounded-lg shadow-card border border-border"
                              loading="lazy"
                            />
                          </div>
                        </div>

                        {/* Step 4 & 5 combined */}
                        <div className="flex flex-col lg:flex-row-reverse items-center gap-6 p-6 bg-secondary/30 rounded-xl border border-border">
                          <div className="flex-1 space-y-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-lg">
                                4
                              </div>
                              <h3 className="text-xl font-bold text-foreground">¡Instalación completa!</h3>
                            </div>
                            <p className="text-lg text-foreground/90 font-semibold text-primary">
                              ¡Listo! DoctIA ya está instalada en tu Word.
                            </p>
                          </div>
                          <div className="w-full lg:w-80 grid grid-cols-2 gap-2">
                            <img
                              src="/images/help/instalacion_office4.jpg"
                              alt="Paso 4: Instalación"
                              className="w-full h-auto rounded-lg shadow-card border border-border"
                              loading="lazy"
                            />
                            <img
                              src="/images/help/instalacion_office5.jpg"
                              alt="Paso 5: Completado"
                              className="w-full h-auto rounded-lg shadow-card border border-border"
                              loading="lazy"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="bg-warning/10 border border-warning/30 rounded-lg p-4">
                        <p className="text-sm text-foreground">
                          <strong>Nota:</strong> Si no la encontrás, es probable que tu versión de Word sea demasiado
                          antigua. Necesitás Word 2016+ o Word 365 para poder usar complementos avanzados.
                        </p>
                      </div>

                      <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
                        <p className="text-lg text-center font-medium">
                          Podés ingresar en la aplicación con el usuario y contraseña que ya utilizás en la versión web.
                        </p>
                      </div>

                      <div className="bg-secondary/30 border border-border rounded-lg p-6 text-center">
                        <div className="flex items-center justify-center gap-2 mb-3">
                          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="text-primary-foreground"
                            >
                              <circle cx="12" cy="12" r="10" />
                              <path d="M12 8v4l2 2" />
                            </svg>
                          </div>
                          <h4 className="text-lg font-semibold text-foreground">¿Todavía tenés dudas?</h4>
                        </div>
                        <p className="text-base text-muted-foreground mb-3">
                          ¡Queremos ayudarte a que tengas la mejor experiencia!
                        </p>
                        <a
                          href="#soporte"
                          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:bg-primary-hover transition-colors"
                        >
                          ¡Contactanos!
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M7 17l9.2-9.2M17 17V7H7" />
                          </svg>
                        </a>
                      </div>

                      {/* Separator */}
                      <div className="border-t-2 border-dashed border-border my-8"></div>

                      {/* How to use in Word */}
                      <div className="space-y-6">
                        <h3 className="text-2xl font-bold text-foreground text-center bg-secondary/50 p-4 rounded-lg border-l-4 border-primary">
                          ¿Cómo usar DoctIA dentro de Word?
                        </h3>
                        <p className="text-lg text-muted-foreground text-center">
                          Una vez instalado, fijate lo que podés hacer sin salir de tu documento.
                        </p>
                      </div>

                      {/* Step 1: Select text */}
                      <div className="space-y-4 p-6 bg-secondary/30 rounded-xl border border-border">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-lg">
                            1
                          </div>
                          <h3 className="text-xl font-bold text-foreground">Resaltá el texto que querés analizar</h3>
                        </div>
                        <p className="text-base text-foreground/90 leading-relaxed">
                          Supongamos que estás trabajando con una demanda de daños y perjuicios en un siniestro vial. En
                          lugar de ir a buscadores tradicionales, escribir palabras clave y rezar para que aparezca
                          algún resultado relevante, simplemente{" "}
                          <strong>resaltás el párrafo que describe los hechos del caso</strong>, como si quisieras
                          ponerlo en negrita.
                        </p>
                        <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                          <p className="text-sm text-foreground">
                            🔒 <strong>Importante:</strong> Ese texto pasa a DoctIA, que{" "}
                            <strong>no lee nada más de tu documento</strong>. Tu privacidad está garantizada.
                          </p>
                        </div>
                      </div>

                      {/* Step 2: Analyze with AI */}
                      <div className="space-y-4 p-6 bg-secondary/30 rounded-xl border border-border">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-lg">
                            2
                          </div>
                          <h3 className="text-xl font-bold text-foreground">Hacé clic en "Analizar con IA"</h3>
                        </div>
                        <p className="text-base text-foreground/90 leading-relaxed">
                          Con un clic en <strong>"Analizar con IA"</strong>, en segundos obtenés fallos relevantes para
                          ese fragmento específico de tu escrito.
                        </p>
                        <p className="text-base text-foreground/90 leading-relaxed">
                          Podés ver un resumen de las cuestiones debatidas en esos casos y las doctrinas o sumarios que
                          surgen de ellos.
                        </p>
                        <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                          <p className="text-sm font-semibold text-foreground">
                            ⚡ DoctIA entiende el contexto de lo que escribiste, no solo palabras sueltas.
                          </p>
                        </div>
                      </div>

                      {/* Step 3: Download and insert */}
                      <div className="space-y-4 p-6 bg-secondary/30 rounded-xl border border-border">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-lg">
                            3
                          </div>
                          <h3 className="text-xl font-bold text-foreground">Descargá, resumí e insertá</h3>
                        </div>
                        <p className="text-base text-foreground/90 leading-relaxed">
                          Con otro clic, <strong>descargás el fallo completo y original</strong> para verificar la
                          información.
                        </p>
                        <p className="text-base text-foreground/90 leading-relaxed">
                          Con otro clic, podés <strong>resumirlo con IA</strong>.
                        </p>
                        <p className="text-base text-foreground/90 leading-relaxed">
                          Y si querés, <strong>insertás directamente la cita en el escrito</strong> que estás
                          redactando.
                        </p>
                        <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                          <p className="text-sm font-semibold text-foreground">
                            ✨ Todo sin salir de Word. Más simple, imposible.
                          </p>
                        </div>
                      </div>

                      {/* Pro tip: multilingual */}
                      <div className="space-y-4 p-6 bg-secondary/30 rounded-xl border border-border">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-lg">
                            💡
                          </div>
                          <h3 className="text-xl font-bold text-foreground">Tip Pro: Búsqueda multiidioma</h3>
                        </div>
                        <p className="text-base text-foreground/90 leading-relaxed">
                          Si estás leyendo o escribiendo un artículo en otro idioma, por ejemplo{" "}
                          <strong>inglés o francés</strong>, y querés buscar jurisprudencia relacionada con lo que dice
                          ese texto, basta con que resaltes la porción relevante y DoctIA te devuelve precedentes{" "}
                          <strong>como si todo estuviera en castellano</strong>.
                        </p>
                        <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                          <p className="text-sm text-foreground">
                            🌍 DoctIA traduce y busca automáticamente, sin que tengas que hacer nada extra.
                          </p>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </section>

        <section id="soporte" className="space-y-6 mt-8 scroll-mt-24" aria-labelledby="soporte">
          <Card>
            <CardContent className="pt-6">
              <Accordion type="single" collapsible defaultValue="">
                <AccordionItem value="soporte" className="border-none">
                  <AccordionTrigger className="text-2xl font-bold hover:no-underline">Soporte</AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-6 pt-4">
                      <p className="text-muted-foreground">
                        ¿Necesitás ayuda? Contactanos a través de nuestros canales de atención al cliente.
                      </p>

                      <div className="grid md:grid-cols-2 gap-6">
                        <a
                          href="https://instagram.com/inteligencialegal.ok"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-4 p-4 rounded-lg border border-border bg-card hover:bg-accent/50 transition-colors group"
                        >
                          <div className="flex-shrink-0 w-12 h-12 bg-primary rounded-xl flex items-center justify-center text-primary-foreground">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                            </svg>
                          </div>
                          <div>
                            <h3 className="font-semibold text-foreground">Instagram</h3>
                            <p className="text-sm text-muted-foreground">@inteligencialegal.ok</p>
                          </div>
                        </a>

                        <a
                          href="mailto:consultas@inteligencialegal.com.ar"
                          className="flex items-center gap-4 p-4 rounded-lg border border-border bg-card hover:bg-accent/50 transition-colors group"
                        >
                          <div className="flex-shrink-0 w-12 h-12 bg-primary rounded-xl flex items-center justify-center text-primary-foreground">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                              <polyline points="22,6 12,13 2,6" />
                            </svg>
                          </div>
                          <div>
                            <h3 className="font-semibold text-foreground">Email</h3>
                            <p className="text-sm text-muted-foreground">consultas@inteligencialegal.com.ar</p>
                          </div>
                        </a>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </section>

        <section
          id="terminos-condiciones"
          className="space-y-6 mt-8 scroll-mt-24"
          aria-labelledby="terminos-condiciones"
        >
          <Card>
            <CardContent className="pt-6">
              <Accordion
                type="single"
                collapsible
                className="w-full"
                value={openTerms ? "terms" : undefined}
                onValueChange={(v) => setOpenTerms(v === "terms")}
              >
                <AccordionItem value="terms" className="border-none">
                  <AccordionTrigger className="text-2xl font-bold hover:no-underline">
                    Términos y Condiciones
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="prose prose-sm dark:prose-invert max-w-none pt-4">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
                        }}
                      >
                        {terms || "Cargando..."}
                      </ReactMarkdown>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </section>

        <section id="privacy" className="space-y-6 mt-8 scroll-mt-24" aria-labelledby="privacy">
          <Card>
            <CardContent className="pt-6">
              <Accordion
                type="single"
                collapsible
                className="w-full"
                value={openPrivacy ? "privacy" : undefined}
                onValueChange={(v) => setOpenPrivacy(v === "privacy")}
              >
                <AccordionItem value="privacy" className="border-none">
                  <AccordionTrigger className="text-2xl font-bold hover:no-underline">
                    Política de Privacidad
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="prose prose-sm dark:prose-invert max-w-none pt-4">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
                        }}
                      >
                        {privacy || "Cargando..."}
                      </ReactMarkdown>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </section>
      </main>
    </>
  );
}
